import React, { memo, useCallback, useMemo } from 'react';
import { type Href, Link, usePathname, useRouter } from 'expo-router';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';

import { ScreenHeader as GarnishScreenHeader, theme, useIsFocused } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useLastInteractedStore } from '../../context/LastInteractedStore';
import { useGroupOrderDetails } from '../../hooks/useGroupOrderDetails';
import { useLoyaltyAvailablePoints } from '../../hooks/useLoyaltyAvailablePoints';
import { LocationTooltip, useLocationTooltip } from '../LocationTooltip';
import { OrderStatusBar } from '../OrderStatusBar';
import {
  useScreenHeaderAddressQuery,
  useScreenHeaderRestaurantQuery,
} from './graphql/ScreenHeader.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const ScreenHeader = memo((props: Props) => {
  const { disclaimer, pendingOrder, rounded, shouldShowLocationTooltip = false } = props;

  const pathname = usePathname();
  const router = useRouter();
  const { track } = useTelemetry();
  const { formatMessage, formatNumber } = useIntl();

  const isLoggedIn = useIsLoggedIn();
  const lastInteractedStore = useLastInteractedStore();
  const { hostGroupOrderDetails, inviteeGroupOrderDetails } = useGroupOrderDetails();
  const points = useLoyaltyAvailablePoints();
  const isFocused = useIsFocused();
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  const addressId = lastInteractedStore?.addressId ?? '';
  const restaurantId = lastInteractedStore?.restaurantId ?? '';

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ data: addressData, fetching: isFetchingAddress }] = useScreenHeaderAddressQuery({
    variables: { addressId },
    pause: !addressId,
    requestPolicy: 'cache-first',
  });

  const [{ data: restaurantData, fetching: isFetchingRestaurant }] = useScreenHeaderRestaurantQuery(
    {
      variables: { id: restaurantId },
      pause: !restaurantId,
      requestPolicy: 'cache-first',
    },
  );
  // ─── Derived ───────────────────────────────────────────────────────────────

  const addressName = addressData?.address?.name;
  const restaurant = restaurantData?.restaurant;

  const orderChannel = useMemo(() => {
    if (addressId) return 'delivery';
    if (restaurant?.isOutpost) return 'outpost';

    return 'pickup';
  }, [addressId, restaurant?.isOutpost]);

  const locationName = addressId ? addressName : restaurant?.name;
  const locationTitle = locationName ?? formatMessage(messages.findLocationNearYou);
  const headerEyebrow = formatMessage(messages.headerEyebrow, { orderChannel });
  const locationTooltipChannel = orderChannel === 'outpost' ? undefined : orderChannel;
  const locationTooltipLocation = addressId ? addressData?.address : restaurant;
  const locationTooltipTarget = locationTooltipLocation ?? undefined;

  const groupOrderHeaderEyebrow = hostGroupOrderDetails
    ? formatMessage(messages.groupOrderHeaderEyebrow, {
        orderChannel: hostGroupOrderDetails.orderChannel,
      })
    : undefined;
  const groupOrderInviteeTitle = inviteeGroupOrderDetails
    ? formatMessage(messages.groupOrderInviteeTitle, {
        host_name: inviteeGroupOrderDetails.hostFirstName,
      })
    : undefined;

  const locationSelectionHref = useMemo<Href>(
    () => ({ pathname: '/location-selection', params: { backToPath: pathname } }),
    [pathname],
  );

  // NOTE: Custom light status bar is needed only on Android. iOS handles color change automatically.
  const shouldRenderCustomStatusBar = Platform.OS === 'android' && isFocused;

  const shouldShowPendingOrder = Boolean(pendingOrder);
  const shouldShowGroupOrderHost = !shouldShowPendingOrder && Boolean(hostGroupOrderDetails);
  const shouldShowGroupOrderInvitee = !shouldShowPendingOrder && Boolean(inviteeGroupOrderDetails);
  const shouldShowLocation =
    !shouldShowPendingOrder && !shouldShowGroupOrderHost && !shouldShowGroupOrderInvitee;
  const headerPalette = getHeaderPalette({
    hasPendingOrder: shouldShowPendingOrder,
    isNavbarV2Enabled,
  });

  const locationTooltip = useLocationTooltip({
    canShow: shouldShowLocationTooltip && shouldShowLocation,
    channel: locationTooltipChannel,
    locationId: addressId || restaurantId,
    targetPosition: locationTooltipTarget,
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleLocationPress = useCallback(() => {
    track('header_location_selected');
    router.navigate(locationSelectionHref);
  }, [locationSelectionHref, router, track]);

  const handleRewardsPress = useCallback(() => {
    track('header_points_cta_selected');

    if (isLoggedIn) {
      router.navigate('/loyalty');

      return;
    }

    router.navigate({
      pathname: '/(auth)/join-or-sign-in',
      params: { redirect: '/' },
    });
  }, [isLoggedIn, router, track]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      {shouldRenderCustomStatusBar ? <OrderStatusBar barStyle="light-content" /> : null}

      <GarnishScreenHeader.Container
        palette={headerPalette}
        rounded={rounded}
        style={styles.container}
      >
        <GarnishScreenHeader.Content>
          {shouldShowPendingOrder && pendingOrder ? (
            <Link asChild href={pendingOrder.href}>
              <TouchableOpacity
                accessibilityLabel={`${pendingOrder.statusLabel}, ${pendingOrder.etaLabel}`}
                accessibilityRole="button"
                activeOpacity={0.8}
                onPress={pendingOrder.onPress}
                style={styles.pendingOrderButton}
              >
                <GarnishScreenHeader.Eyebrow>
                  {pendingOrder.statusLabel}
                </GarnishScreenHeader.Eyebrow>

                <GarnishScreenHeader.Title>{pendingOrder.etaLabel}</GarnishScreenHeader.Title>
              </TouchableOpacity>
            </Link>
          ) : null}

          {shouldShowGroupOrderHost && hostGroupOrderDetails && groupOrderHeaderEyebrow ? (
            <GarnishScreenHeader.TitleContainer>
              <GarnishScreenHeader.Eyebrow>{groupOrderHeaderEyebrow}</GarnishScreenHeader.Eyebrow>

              <GarnishScreenHeader.Title>
                {hostGroupOrderDetails.locationName}
              </GarnishScreenHeader.Title>
            </GarnishScreenHeader.TitleContainer>
          ) : null}

          {shouldShowGroupOrderInvitee && groupOrderInviteeTitle ? (
            <GarnishScreenHeader.TitleContainer>
              <GarnishScreenHeader.Title>{groupOrderInviteeTitle}</GarnishScreenHeader.Title>
            </GarnishScreenHeader.TitleContainer>
          ) : null}

          {shouldShowLocation ? (
            <GarnishScreenHeader.TitleContainer>
              {lastInteractedStore ? (
                <GarnishScreenHeader.Eyebrow>{headerEyebrow}</GarnishScreenHeader.Eyebrow>
              ) : null}

              {isFetchingAddress || isFetchingRestaurant ? null : (
                <>
                  <GarnishScreenHeader.LocationButton onPress={handleLocationPress}>
                    {locationTitle}
                  </GarnishScreenHeader.LocationButton>

                  <LocationTooltip
                    isVisible={locationTooltip.isVisible}
                    orderChannel={orderChannel}
                    horizontalAlignment="start"
                    topOffset={LOCATION_TOOLTIP_TOP_OFFSET}
                    onDismiss={locationTooltip.dismiss}
                  />
                </>
              )}
            </GarnishScreenHeader.TitleContainer>
          ) : null}

          {isLoggedIn ? (
            <GarnishScreenHeader.PointsButton
              points={formatNumber(points)}
              text={formatMessage(messages.points)}
              onPress={handleRewardsPress}
            />
          ) : (
            <GarnishScreenHeader.PointsButton
              text={formatMessage(messages.signIn)}
              onPress={handleRewardsPress}
            />
          )}
        </GarnishScreenHeader.Content>
      </GarnishScreenHeader.Container>

      {disclaimer ? (
        <GarnishScreenHeader.Disclaimer disclaimer={disclaimer} rounded={rounded} />
      ) : null}
    </>
  );
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getHeaderPalette(params: GetHeaderPaletteParams) {
  const { hasPendingOrder, isNavbarV2Enabled } = params;

  if (hasPendingOrder) return 'spinach';

  return isNavbarV2Enabled ? 'kale' : 'brown';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    zIndex: theme.zIndex.fixed,
  },
  pendingOrderButton: {
    flexShrink: 1,
    alignItems: 'flex-start',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly groupOrderHeaderEyebrow: { readonly orderChannel: string };
  readonly groupOrderInviteeTitle: { readonly host_name: MessageValue };
  readonly headerEyebrow: { readonly orderChannel: string };
  readonly findLocationNearYou: NoMessageValues;
  readonly points: NoMessageValues;
  readonly signIn: NoMessageValues;
}>({
  groupOrderHeaderEyebrow: {
    defaultMessage:
      'Group Order - {orderChannel, select, delivery {Deliver to:} pickup {Pickup from:} outpost {Outpost at:} other {}}',
    description: 'Screen header | Host group order location eyebrow',
  },
  groupOrderInviteeTitle: {
    defaultMessage: "{host_name}'s Group Order",
    description: 'Screen header | Invitee group order title',
  },
  headerEyebrow: {
    defaultMessage:
      '{orderChannel, select, delivery {Deliver to} pickup {Pickup from} outpost {Outpost at} other {Deliver to}}',
    description: 'Screen header | Location eyebrow',
  },
  findLocationNearYou: {
    defaultMessage: 'Find location near you',
    description: 'Screen header | Find location near you button',
  },
  points: {
    defaultMessage: 'pts',
    description: 'Screen header | Points button',
  },
  signIn: {
    defaultMessage: 'Sign in',
    description: 'Screen header | Sign in button',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATION_TOOLTIP_TOP_OFFSET: number = Platform.select({
  native: -theme.spacing['4'],
  default: 0,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  disclaimer?: string;
  pendingOrder?: PendingOrder;
  rounded?: boolean;
  shouldShowLocationTooltip?: boolean;
};

type PendingOrder = {
  etaLabel: string;
  href: Href;
  statusLabel: string;
  onPress: () => void;
};

type GetHeaderPaletteParams = {
  hasPendingOrder: boolean;
  isNavbarV2Enabled: boolean;
};
